interface SEOSchemaProps {
  type: 'homepage' | 'article' | 'page';
  title?: string;
  description?: string;
  url?: string;
  dateModified?: string;
  articleData?: {
    author: string;
    datePublished: string;
    image: string;
  };
}

export function getSchemaScript(props: SEOSchemaProps): string {
  const { type, title, description, url, dateModified, articleData } = props;

  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuy.boutique';
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'KakobuySheet',
    url: baseUrl,
    description: 'Your trusted resource for the latest Kakobuy Spreadsheet links, product finds, and shopping guides.',
    sameAs: ['https://kaspreadsheet.com/'],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'KakobuySheet - Kakobuy Spreadsheet Resource Center',
    url: baseUrl,
    description: 'Latest Kakobuy Spreadsheet links, best finds, buying guides, and trusted shopping resources.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: baseUrl,
      },
      ...(type !== 'homepage' ? [{
        '@type': 'ListItem',
        position: 2,
        name: title || 'Page',
        item: url || baseUrl,
      }] : []),
    ],
  };

  const schemas = [organizationSchema, websiteSchema, breadcrumbSchema];

  if (type === 'homepage') {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Kakobuy Spreadsheet: The Ultimate Resource Center 2026',
      description: description || 'Explore the latest Kakobuy Spreadsheet links, best product finds, buying guides, and trusted shopping resources. Updated daily.',
      url: url || baseUrl,
      dateModified: dateModified || '2026-06-25',
      about: {
        '@type': 'Thing',
        name: 'Kakobuy Spreadsheet',
        description: 'A comprehensive, regularly updated resource that organizes product links, seller information, and shopping references for cross-border shoppers.',
      },
    });

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is the Kakobuy Spreadsheet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The Kakobuy Spreadsheet is a comprehensive, regularly updated resource that organizes thousands of product links, seller information, and shopping references into one easy-to-navigate document. It helps shoppers quickly discover products across shoes, clothing, accessories, bags, and more.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is the Kakobuy Spreadsheet safe to use?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, the Kakobuy Spreadsheet is designed with safety in mind. All spreadsheet links go through a verification process before being added to our directory. We only include resources from trusted sellers and community-vetted sources.',
          },
        },
        {
          '@type': 'Question',
          name: 'How often is the Kakobuy Spreadsheet updated?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The Kakobuy Spreadsheet is updated on a daily basis to ensure you always have access to the freshest links and most current product information.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I use the Kakobuy Spreadsheet for shopping?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Simply browse our category directory to find the product type you are interested in, click through to the relevant spreadsheet page on kaspreadsheet.com, and explore the organized product links.',
          },
        },
        {
          '@type': 'Question',
          name: 'What categories are available in the Kakobuy Spreadsheet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The Kakobuy Spreadsheet covers Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and more.',
          },
        },
        {
          '@type': 'Question',
          name: 'Why should I use the Kakobuy Spreadsheet over searching individually?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The Kakobuy Spreadsheet saves significant time by consolidating thousands of product links into organized categories. Instead of hours searching across platforms, you find curated, verified products in one place.',
          },
        },
      ],
    });
  }

  if (type === 'article' && articleData) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title || '',
      description: description || '',
      author: {
        '@type': 'Organization',
        name: articleData.author,
      },
      datePublished: articleData.datePublished,
      dateModified: dateModified || articleData.datePublished,
      image: articleData.image,
      url: url || baseUrl,
      publisher: {
        '@type': 'Organization',
        name: 'KakobuySheet',
        url: baseUrl,
      },
    });
  }

  if (type === 'page') {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title || 'Page',
      description: description || '',
      url: url || baseUrl,
      dateModified: dateModified || '2026-06-25',
      about: {
        '@type': 'Thing',
        name: 'Kakobuy Spreadsheet',
      },
    });
  }

  return JSON.stringify(schemas);
}

export default function SEOSchema({ type, title, description, url, dateModified, articleData }: SEOSchemaProps) {
  const scriptContent = getSchemaScript({ type, title, description, url, dateModified, articleData });
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: scriptContent }}
    />
  );
}